import React, { useEffect, useState } from 'react';
import { Button, LoadingState, SectionHeader } from '../common';
import { getPlayerNameColor } from '../../utils/playerUtils';
import { getRankTier, RANK_TIER_CLASSES } from '../../utils/leaderboardRankTiers';
import {
  DEFAULT_MATCHUP_HIGHLIGHT_MIN_GAMES,
  MATCHUP_HIGHLIGHT_MIN_GAMES_OPTIONS,
  MATCHUP_HIGHLIGHT_PREVIEW,
} from '../../utils/matchupHighlights';

function formatWinRate(pct) {
  if (pct == null || Number.isNaN(pct)) return '—';
  return `${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%`;
}

function formatOrdinal(rank) {
  const n = Number(rank);
  if (!n) return '';
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function RankBadge({ rank }) {
  const ordinal = formatOrdinal(rank);
  const tier = getRankTier(rank, 'preview');
  const tierRankClass = tier ? RANK_TIER_CLASSES[tier].rank : 'border-border-medium text-text-primary';

  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full border-2 h-8 min-w-[2.25rem] px-1 text-[10px] sm:text-xs font-semibold ${tierRankClass}`}
      aria-label={`Rank ${ordinal}`}
    >
      {ordinal}
    </span>
  );
}

function HighlightRow({ row, rank, onSelect, selectLabel }) {
  const ordinal = formatOrdinal(rank);
  const tier = getRankTier(rank, 'preview');
  const cardClass = tier
    ? RANK_TIER_CLASSES[tier].card
    : 'bg-surface border border-border-light';

  return (
    <button
      type="button"
      onClick={() => onSelect(row)}
      className={`w-full min-h-[44px] flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-primary text-left ${cardClass}`}
      aria-label={`${ordinal}. ${selectLabel} ${row.name}, ${row.wins} wins ${row.losses} losses, ${formatWinRate(row.winRate)}`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <RankBadge rank={rank} />
        <span className={`text-sm font-semibold truncate ${getPlayerNameColor(row.gender)}`}>
          {row.name}
        </span>
      </span>
      <span className="shrink-0 text-xs sm:text-sm tabular-nums text-text-secondary">
        {row.wins}–{row.losses}
        <span className="text-text-muted"> · {formatWinRate(row.winRate)}</span>
        <span className="hidden sm:inline text-text-muted"> · {row.games} games</span>
      </span>
    </button>
  );
}

function HighlightColumn({ title, rows, emptyLabel, onSelect, selectLabel, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAll, setShowAll] = useState(false);
  const visibleRows = showAll ? rows : rows.slice(0, MATCHUP_HIGHLIGHT_PREVIEW);
  const canShowMore = rows.length > MATCHUP_HIGHLIGHT_PREVIEW;

  return (
    <div className="min-w-0 rounded-lg border border-border-light bg-surface/60 overflow-hidden">
      <button
        type="button"
        className="w-full min-h-[44px] flex items-center justify-between gap-2 px-2.5 py-2 text-left hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-text-muted">
          {title}
          {rows.length > 0 ? (
            <span className="ml-1.5 font-semibold text-text-secondary normal-case tracking-normal">
              {rows.length}
            </span>
          ) : null}
        </span>
        <svg
          className={`w-5 h-5 text-text-muted shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded ? (
        <div className="px-2.5 pb-2.5">
          {rows.length === 0 ? (
            <p className="text-xs text-text-muted py-2">{emptyLabel}</p>
          ) : (
            <>
              <ul className="space-y-1.5">
                {visibleRows.map((row, index) => (
                  <li key={row.id}>
                    <HighlightRow
                      row={row}
                      rank={index + 1}
                      onSelect={onSelect}
                      selectLabel={selectLabel}
                    />
                  </li>
                ))}
              </ul>
              {canShowMore ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  className="w-full mt-1.5 text-xs font-semibold"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAll((v) => !v);
                  }}
                >
                  {showAll ? 'Show less' : `Show more (${rows.length - MATCHUP_HIGHLIGHT_PREVIEW})`}
                </Button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function toPickerPlayer(row) {
  return {
    id: row.id,
    first_name: row.first_name,
    last_name: row.last_name,
    gender: row.gender,
    name: row.name,
  };
}

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`w-5 h-5 text-text-muted shrink-0 transition-transform duration-200 ${
        expanded ? 'rotate-180' : ''
      }`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function MinGamesSelect({ minGames, onMinGamesChange }) {
  return (
    <label className="flex items-center gap-1.5 shrink-0 pt-0.5">
      <span className="text-[10px] sm:text-xs font-medium text-text-secondary whitespace-nowrap">
        Min Games
      </span>
      <select
        value={minGames}
        onChange={(e) => onMinGamesChange(Number(e.target.value))}
        className="form-input text-xs py-1 pl-2 pr-7 w-[4.5rem]"
        aria-label="Minimum games"
      >
        {MATCHUP_HIGHLIGHT_MIN_GAMES_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </label>
  );
}

export function MatchupHighlights({
  bestWith = [],
  bestAgainst = [],
  minGames = DEFAULT_MATCHUP_HIGHLIGHT_MIN_GAMES,
  onMinGamesChange,
  isLoading = false,
  hasSelections = false,
  onSelectTeammate,
  onSelectOpponent,
}) {
  const bothEmpty = bestWith.length === 0 && bestAgainst.length === 0;
  const [expanded, setExpanded] = useState(!hasSelections);

  useEffect(() => {
    setExpanded(!hasSelections);
  }, [hasSelections]);

  return (
    <div
      className="rounded-xl border border-border-medium bg-surface-subtle mb-3 sm:mb-4 overflow-hidden"
      aria-label="Best records"
    >
      <div className="flex items-start gap-2 px-3 py-3 sm:px-4 sm:py-3.5">
        <button
          type="button"
          className="min-w-0 flex-1 text-left rounded-lg hover:bg-surface-subtle/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="matchup-best-records-body"
        >
          <SectionHeader
            title="Best records"
            subtitle="Highest win rates at this club"
            size="sm"
            className="mb-0 sm:mb-0 min-w-0"
          />
        </button>
        {expanded ? (
          <MinGamesSelect minGames={minGames} onMinGamesChange={onMinGamesChange} />
        ) : null}
        <button
          type="button"
          className="shrink-0 p-1 mt-0.5 rounded-lg hover:bg-surface-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="matchup-best-records-body"
          aria-label={expanded ? 'Collapse best records' : 'Expand best records'}
        >
          <ChevronIcon expanded={expanded} />
        </button>
      </div>

      <div
        id="matchup-best-records-body"
        aria-hidden={!expanded}
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        }`}
      >
        <div className="px-3 pb-3 sm:px-4 sm:pb-3.5">
          {isLoading && bothEmpty ? (
            <LoadingState message="Loading win rates…" size="small" className="py-4" />
          ) : bothEmpty ? (
            <p className="text-sm text-text-muted py-2">
              Play at least {minGames} games with or against someone to see your best records here.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <HighlightColumn
                title="Best with"
                rows={bestWith}
                emptyLabel={`No teammates with at least ${minGames} games`}
                selectLabel="View games with"
                onSelect={(row) => onSelectTeammate(toPickerPlayer(row))}
              />
              <HighlightColumn
                title="Best against"
                rows={bestAgainst}
                emptyLabel={`No opponents with at least ${minGames} games`}
                selectLabel="View games against"
                onSelect={(row) => onSelectOpponent(toPickerPlayer(row))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MatchupHighlights;
